import { Auditing } from './auditing.model';
import { FormBuilder, FormControl, FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { BillableItemType, FlatFeeType } from '@ee/common/enums';
import { SimpleUser } from './simple-user.model';

export interface BillableItem extends Auditing {
  id?: string;
  case_id?: string;
  case_closed_date?: string;
  type?: BillableItemType;
  flat_fee_type?: FlatFeeType;
  date_incurred?: Date;
  description?: string;
  save_description?: boolean;
  qty?: number;
  rate?: number;
  amount?: number;
  paid?: number;
  invoice_id?: string;
  invoice_number?: string;
  invoice_add_date?: string;
  street_address?: string;
  property_unit_type?: string;
  property_unit_number?: string;
  tenants?: string;
  docket_number?: string;
  selected?: boolean;
  custom?: boolean;
  deleted?: boolean;
  deleted_by?: string;
  deleted_date?: string;
  // Check request fields
  request_check?: boolean;
  check_payee?: string;
  check_payee_id?: string;
  check_entry_id?: string;
  check_processed_date?: string;
  check_processed_by?: string;
  check_denied?: boolean;
  // Audit trail for check status changes
  check_status_audit?: CheckStatusAuditEntry[];
  // Performed by fields
  performed_by?: string;
  performed_by_user_id?: string;
  // Category fields
  category_id?: string;
  category_name?: string;
  cost_type?: string;
}

export interface CheckStatusAuditEntry {
  action: 'PROCESSED' | 'DENIED' | 'RESET';
  timestamp: string;
  user: string;
  previous_values?: {
    check_payee?: string;
    check_entry_id?: string;
    check_processed_date?: string;
    check_processed_by?: string;
    check_denied?: boolean | null;
  };
}

export type BillableItemFormType = FormGroup<{
  id: FormControl<string | null>;
  case_id: FormControl<string | null>;
  type: FormControl<BillableItemType>;
  flat_fee_type: FormControl<FlatFeeType | null>;
  date_incurred: FormControl<Date>;
  description: FormControl<string | null>;
  save_description: FormControl<boolean>;
  qty: FormControl<number | null>;
  rate: FormControl<number | null>;
  amount: FormControl<number | null>;
  paid: FormControl<number | null>;
  invoice_id: FormControl<string | null>;
  invoice_add_date: FormControl<string | null>;
  // Check request fields
  request_check: FormControl<boolean>;
  check_payee: FormControl<string | null>;
  check_payee_id: FormControl<string | null>;
  check_processed_date: FormControl<string | null>;
  check_processed_by: FormControl<string | null>;
  check_denied: FormControl<boolean>;
  // Performed by fields
  performed_by: FormControl<string | null>;
  performed_by_user_id: FormControl<string | null>;
  // Category fields
  category_id: FormControl<string | null>;
  cost_type: FormControl<string | null>;
}>

export function GenerateBillableItemForm(fb: FormBuilder, value: BillableItem = { type: BillableItemType.FLAT_FEE },
  autoBillableSetup = false, currentUser?: SimpleUser): BillableItemFormType {
  const form: BillableItemFormType = fb.group({
    id: new FormControl<string | null>(null, { nonNullable: false }),
    case_id: new FormControl<string | null>(null),
    type: new FormControl<BillableItemType>(BillableItemType.FLAT_FEE, Validators.required),
    flat_fee_type: new FormControl<FlatFeeType | null>(FlatFeeType.CUSTOM),
    date_incurred: new FormControl<Date>(new Date(), autoBillableSetup ? null : Validators.required),
    description: new FormControl<string | null>(null),
    save_description: new FormControl<boolean>(false),
    qty: new FormControl<number | null>(null),
    rate: new FormControl<number | null>(null),
    amount: new FormControl<number | null>(null, Validators.required),
    paid: new FormControl<number | null>(null),
    invoice_id: new FormControl<string | null>(null),
    invoice_add_date: new FormControl<string | null>(null),
    // Check request fields
    request_check: new FormControl<boolean>(false, { nonNullable: true }),
    check_payee: new FormControl<string | null>(null),
    check_payee_id: new FormControl<string | null>(null),
    check_processed_date: new FormControl<string | null>(null),
    check_processed_by: new FormControl<string | null>(null),
    check_denied: new FormControl<boolean>(false, { nonNullable: true }),
    // Performed by fields
    performed_by: new FormControl<string | null>(currentUser?.display_name ?? null),
    performed_by_user_id: new FormControl<string | null>(currentUser?.id ?? null),
    // Category fields
    category_id: new FormControl<string | null>(null),
    cost_type: new FormControl<string | null>(null)
  });

  if (value) {
    form.patchValue(value);
    if (!form.value.date_incurred) { // set the date incurred to today
      form.get('date_incurred').setValue(new Date());
    }
  }

  return form;
}

export function GenerateAutoBillableForm(fb: UntypedFormBuilder, value: BillableItem = null) {
  const calculatedFeeTypes: FlatFeeType[] = [FlatFeeType.COUNTY_FEE, FlatFeeType.WRIT_FEE, FlatFeeType.SHERIFF_FEE, FlatFeeType.HEARING_FEE, FlatFeeType.CLIENT_ATTORNEY_FEE];
  const amountValidator = !value || !calculatedFeeTypes.includes(value.flat_fee_type)
    ? Validators.required : null;
  const form = fb.group({
    type: [null, Validators.required],
    flat_fee_type: [null],
    description: [null],
    amount: [null, amountValidator],
    request_check: [false],
    category_id: [null],
    cost_type: [null]
  });

  if (value) {
    form.reset(value);
  }

  return form;
}
